import { generateReportBuilderTemplateName } from "$root/functional-areas/pdf";
import type { TemplateChooserSubmitParams } from "$root/functional-areas/portfolio-studio/dialogs/TemplateChooserDialog";
import type { StoryfolioV2Template } from "$root/functional-areas/reports/export-templates";
import { downloadBlob, downloadUrl } from "$root/utils";
import { unreachable } from "@mdotm/utils/runtime";
import { GenerateArtifactRequestStageEnum, storyfolioV2Repository, type BulkDocumentOutcome } from "./repository";

// Downloading a Storyfolio V2 document into the flow the platform already has: the gateway
// renders it, and everything the user sees — the file's name, the moment it arrives — stays the
// platform's convention rather than the gateway's.

/** One portfolio, as a download needs to name it, and the chooser's choice of composition: current, or its proposal. */
export type StoryfolioV2DownloadTarget = TemplateChooserSubmitParams;

/**
 * The request's `stage`, the one place a choice becomes the gateway's vocabulary. Only a proposal names one: the
 * current composition is the gateway's default, and leaving the field out keeps a current request identical for a
 * gateway that does not read it.
 */
function stageField(choice: StoryfolioV2DownloadTarget["choice"]): {
	stage?: typeof GenerateArtifactRequestStageEnum.Proposal;
} {
	switch (choice) {
		case "current":
			return {};
		case "enhance":
			return { stage: GenerateArtifactRequestStageEnum.Proposal };
		default:
			return unreachable(choice);
	}
}

/** What one target's document is generated from, alike for a single download and for each bulk entry. */
function documentRequest(
	template: StoryfolioV2Template,
	portfolio: StoryfolioV2DownloadTarget,
): { investmentId: string; templateId: string } & ReturnType<typeof stageField> {
	return { investmentId: portfolio.uuid, templateId: template.templateId, ...stageField(portfolio.choice) };
}

/**
 * The file name a generated document lands under.
 *
 * `generateReportBuilderTemplateName` is the platform's convention and is shared deliberately: a
 * document a user cannot tell apart in the menu must not become one they can tell apart in their
 * downloads folder. A proposal is named as the Report Builder names one.
 */
function documentFileName(template: StoryfolioV2Template, portfolio: StoryfolioV2DownloadTarget): string {
	return `${generateReportBuilderTemplateName(portfolio, template.templateName)}.${template.format}`;
}

/** Generate one document for one portfolio, on the composition its target chose, and hand it to the browser. */
export async function downloadStoryfolioV2Document(
	template: StoryfolioV2Template,
	portfolio: StoryfolioV2DownloadTarget,
): Promise<void> {
	const blob = await storyfolioV2Repository.generate(documentRequest(template, portfolio));
	downloadBlob(blob, { fileName: documentFileName(template, portfolio) });
}

export type StoryfolioV2BulkResult = {
	/** The targets whose document was produced. */
	succeeded: StoryfolioV2DownloadTarget[];
	/** The targets whose document was not, each with the gateway's stated reason. */
	failed: Array<StoryfolioV2DownloadTarget & { reason?: string }>;
};

/**
 * Generate one document per target and hand the archive to the browser.
 *
 * One batch, not N single calls: the gateway takes the whole selection and answers with one archive
 * plus one outcome per requested document. A document that fails does not fail the batch — the
 * archive holds the rest — so the result names both halves and the caller reports which portfolios
 * did not make it.
 *
 * Every entry names the same template, so outcomes map back to targets by request order, which the
 * contract guarantees the outcomes are returned in. A portfolio appears once per choice it is asked on.
 *
 * The archive is a cross-origin presigned link, so it saves under the name its
 * `Content-Disposition` gives, not the platform's naming convention.
 */
export async function downloadStoryfolioV2Bulk(
	template: StoryfolioV2Template,
	portfolios: StoryfolioV2DownloadTarget[],
): Promise<StoryfolioV2BulkResult> {
	const { archiveUrl, outcomes } = await storyfolioV2Repository.generateBulk(
		portfolios.map((portfolio) => documentRequest(template, portfolio)),
	);

	const result = splitOutcomes(portfolios, outcomes);
	if (result.succeeded.length > 0) {
		downloadUrl(archiveUrl);
	}
	return result;
}

/**
 * Pair each requested portfolio with what the gateway made of it.
 *
 * Matched on `index`, the 1-based position of the entry in the request, rather than on
 * `investmentId`: the contract makes the index the one key that survives a re-sorted array, and an
 * outcome the gateway returns for an unknown position is dropped rather than blamed on a portfolio
 * that did not ask for it.
 */
function splitOutcomes(
	portfolios: StoryfolioV2DownloadTarget[],
	outcomes: BulkDocumentOutcome[],
): StoryfolioV2BulkResult {
	const result: StoryfolioV2BulkResult = { succeeded: [], failed: [] };
	for (const outcome of outcomes) {
		const portfolio = portfolios[outcome.index - 1];
		if (!portfolio) {
			continue;
		}
		if (outcome.status === "SUCCEEDED") {
			result.succeeded.push(portfolio);
		} else {
			result.failed.push({ ...portfolio, ...(outcome.reason ? { reason: outcome.reason } : {}) });
		}
	}
	return result;
}
